/**
 * Analyse des réponses de l'assistant IA pour les éditeurs (tableur et traitement de texte).
 * L'IA renvoie soit un bloc JSON structuré (tableau + graphique), soit un tableau
 * Markdown, soit du texte libre.
 */

export interface AiTableSpec {
  headers: string[];
  rows: string[][];
}

export type AiChartKind = 'bar' | 'pie' | 'line';

export interface AiChartSeries {
  name: string;
  values: number[];
}

export interface AiChartSpec {
  kind: AiChartKind;
  title: string;
  categories: string[];
  series: AiChartSeries[];
}

export interface AiPayload {
  table: AiTableSpec | null;
  chart: AiChartSpec | null;
  note: string;
  /** Texte lisible (sans le JSON technique) */
  text: string;
}

function toStringMatrix(value: unknown): string[][] {
  if (!Array.isArray(value)) return [];
  return value.map((row) =>
    Array.isArray(row) ? row.map((cell) => (cell === null || cell === undefined ? '' : String(cell))) : [String(row)],
  );
}

function normalizeChart(raw: unknown): AiChartSpec | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const categories = Array.isArray(obj.categories) ? obj.categories.map((c) => String(c)) : [];
  const seriesRaw = Array.isArray(obj.series) ? obj.series : [];
  const series: AiChartSeries[] = seriesRaw
    .map((s) => {
      const item = s as Record<string, unknown>;
      const values = Array.isArray(item?.values) ? item.values.map((v) => Number(v)) : [];
      return { name: String(item?.name ?? 'Série'), values: values.filter((v) => Number.isFinite(v)) };
    })
    .filter((s) => s.values.length > 0);
  if (!series.length) return null;
  const kindRaw = String(obj.kind ?? 'bar').toLowerCase();
  const kind: AiChartKind = kindRaw === 'pie' || kindRaw === 'line' ? (kindRaw as AiChartKind) : 'bar';
  return {
    kind,
    title: String(obj.title ?? 'Graphique'),
    categories: categories.length ? categories : series[0].values.map((_, i) => `Série ${i + 1}`),
    series,
  };
}

/** Extrait le premier objet JSON équilibré d'un texte. */
export function extractJsonObject(text: string): string | null {
  const fence = /```(?:json)?\s*\n?([\s\S]*?)```/i.exec(text);
  const candidates: string[] = [];
  if (fence) candidates.push(fence[1]);
  const start = text.indexOf('{');
  if (start >= 0) {
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < text.length; i += 1) {
      const ch = text[i];
      if (inString) {
        if (escaped) escaped = false;
        else if (ch === '\\') escaped = true;
        else if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') inString = true;
      else if (ch === '{') depth += 1;
      else if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          candidates.push(text.slice(start, i + 1));
          break;
        }
      }
    }
  }
  for (const candidate of candidates) {
    try {
      JSON.parse(candidate);
      return candidate;
    } catch {
      /* on essaie le candidat suivant */
    }
  }
  return null;
}

/** Convertit un tableau Markdown en spécification de tableau. */
export function markdownTableToSpec(text: string): AiTableSpec | null {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().startsWith('|'));
  if (lines.length < 2) return null;
  const split = (line: string) =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((c) => c.trim());
  const headers = split(lines[0]);
  const body = lines.slice(1).filter((l) => !/^\|?[\s:|-]+\|?$/.test(l.trim()));
  const rows = body.map(split).filter((r) => r.some((c) => c !== ''));
  if (!headers.length || !rows.length) return null;
  const width = Math.max(headers.length, ...rows.map((r) => r.length));
  const paddedHeaders = Array.from({ length: width }, (_, i) => headers[i] ?? '');
  const paddedRows = rows.map((r) => Array.from({ length: width }, (_, i) => r[i] ?? ''));
  return { headers: paddedHeaders, rows: paddedRows };
}

/** Nettoie le texte affiché dans la conversation (retire les blocs techniques). */
function cleanDisplayText(raw: string): string {
  let text = raw;
  const json = extractJsonObject(raw);
  if (json) text = text.replace(json, '');
  text = text.replace(/```[a-zA-Z]*/g, '').replace(/```/g, '');
  return text.trim();
}

export function parseAiPayload(raw: string): AiPayload {
  const empty: AiPayload = { table: null, chart: null, note: '', text: (raw || '').trim() };
  if (!raw || !raw.trim()) return empty;

  const json = extractJsonObject(raw);
  if (json) {
    try {
      const parsed = JSON.parse(json) as Record<string, unknown>;
      const tableRaw = (parsed.table ?? parsed.tableau) as Record<string, unknown> | undefined;
      let table: AiTableSpec | null = null;
      if (tableRaw && typeof tableRaw === 'object') {
        const headers = Array.isArray(tableRaw.headers)
          ? tableRaw.headers.map((h) => String(h))
          : Array.isArray(tableRaw.columns)
            ? (tableRaw.columns as unknown[]).map((h) => String(h))
            : [];
        const rows = toStringMatrix(tableRaw.rows);
        if (rows.length) {
          const width = Math.max(headers.length, ...rows.map((r) => r.length));
          table = {
            headers: Array.from({ length: width }, (_, i) => headers[i] ?? ''),
            rows: rows.map((r) => Array.from({ length: width }, (_, i) => r[i] ?? '')),
          };
        }
      }
      const chart = normalizeChart(parsed.chart ?? parsed.graphique);
      const note = String(parsed.note ?? parsed.summary ?? parsed.resume ?? '');
      const text = cleanDisplayText(raw);
      if (table || chart) {
        return { table, chart, note, text: text || note };
      }
    } catch {
      /* on retombe sur le Markdown */
    }
  }

  const table = markdownTableToSpec(raw);
  return { table, chart: null, note: '', text: cleanDisplayText(raw) };
}

/** Sérialise les N derniers messages pour le contexte envoyé au backend. */
export function buildChatHistory(
  messages: { role: 'user' | 'assistant'; content: string }[],
  limit = 8,
): { role: 'user' | 'assistant'; content: string }[] {
  return messages.slice(-limit).map((m) => ({ role: m.role, content: m.content }));
}